import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import '../styles/NotificationBell.css';

const CATEGORY_META = {
  standout:         { label: 'Standout Submission', icon: '⭐' },
  nomination:       { label: 'Spotlight Nomination', icon: '🏅' },
  organization:     { label: 'Organization Request', icon: '🏢' },
  parent_approval:  { label: 'Parent Approval', icon: '👤' },
  coach_verification: { label: 'Coach Verification', icon: '✅' },
  flagged_image:    { label: 'Flagged Image', icon: '🖼️' },
  flagged_video:    { label: 'Flagged Video', icon: '🎥' },
  stats_verification: { label: 'Stats Verification', icon: '📊' },
  stats_dispute:    { label: 'Stats Dispute', icon: '⚠️' },
  profanity:        { label: 'Profanity Detected', icon: '🚫' },
  failed_payment:   { label: 'Failed Payment', icon: '💳' },
  failed_payout:    { label: 'Failed Payout', icon: '💸' },
};

const timeAgo = (dateStr) => {
  const seconds = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const { getAdminNotifications, markNotificationRead, markAllNotificationsRead } = useAuth();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);
  const intervalRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    const result = await getAdminNotifications(40);
    if (result.success) setNotifications(result.data);
    setLoading(false);
  }, [getAdminNotifications]);

  useEffect(() => {
    fetchNotifications();
    intervalRef.current = setInterval(fetchNotifications, 90000);
    return () => clearInterval(intervalRef.current);
  }, [fetchNotifications]);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const handleNotifClick = async (notif) => {
    if (!notif.is_read) {
      await markNotificationRead(notif.id);
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
    }
    if (notif.link) {
      setOpen(false);
      navigate(notif.link);
    }
  };

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead();
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
  };

  const unread = notifications.filter(n => !n.is_read);
  const read = notifications.filter(n => n.is_read);

  return (
    <div className="nb-wrapper" ref={dropdownRef}>
      <button
        className={`nb-bell ${unreadCount > 0 ? 'nb-bell--active' : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-label="Notifications"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="nb-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {open && (
        <div className="nb-dropdown">
          <div className="nb-header">
            <span className="nb-header-title">Notifications</span>
            {unreadCount > 0 && (
              <span className="nb-header-badge">{unreadCount} new</span>
            )}
            <button className="nb-refresh" onClick={fetchNotifications} title="Refresh">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
            </button>
            {unreadCount > 0 && (
              <button className="nb-mark-all" onClick={handleMarkAllRead} title="Mark all read">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </button>
            )}
          </div>

          <div className="nb-list">
            {loading ? (
              <p className="nb-empty">Loading…</p>
            ) : notifications.length === 0 ? (
              <p className="nb-empty">No notifications yet</p>
            ) : (
              <>
                {unread.length > 0 && (
                  <>
                    <div className="nb-section-label">New</div>
                    {unread.map(n => (
                      <NotifRow key={n.id} n={n} onClick={() => handleNotifClick(n)} />
                    ))}
                  </>
                )}
                {read.length > 0 && (
                  <>
                    <div className="nb-section-label nb-section-label--read">Earlier</div>
                    {read.map(n => (
                      <NotifRow key={n.id} n={n} onClick={() => handleNotifClick(n)} />
                    ))}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const NotifRow = ({ n, onClick }) => {
  const meta = CATEGORY_META[n.category] || { label: n.category, icon: '🔔' };
  return (
    <button className={`nb-item ${!n.is_read ? 'nb-item--unread' : ''}`} onClick={onClick}>
      <span className="nb-item-icon-emoji">{meta.icon}</span>
      <span className="nb-item-text">
        <span className="nb-item-title">{n.title}</span>
        {n.description && <span className="nb-item-desc">{n.description}</span>}
        <span className="nb-item-time">{timeAgo(n.created_at)}</span>
      </span>
      {!n.is_read && <span className="nb-unread-dot" />}
    </button>
  );
};

export default NotificationBell;
